import { Project, Target } from '@griply/assets';
import { GoalType } from '@griply/types';
import {
  useCreateSubGoalAllowed,
  useCreateUnlimitedGoalsAllowed,
} from 'features/goals';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, IconProps } from 'shared/components/ui/icon';
import {
  PopupMenuButton,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import {
  CreateGoalOptions,
  useOpenCreateGoalDialog,
  useOpenCreateProjectDialog,
} from 'shared/contexts/goal-form';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { ID } from 'shared/types/id';
import { Timestamp } from 'shared/types/timestamp';

export type AddGoalMenuItemsProps = {
  parentIds: ID[];
  lifeAreaId?: ID | null;
  deadline?: Timestamp;
  onClose?: () => void;
  // one icon for both rows where the entity icons would collide with neighbouring rows
  icon?: IconProps['icon'];
};

export const AddGoalMenuItems: React.FC<AddGoalMenuItemsProps> = ({
  parentIds,
  lifeAreaId,
  deadline,
  onClose,
  icon,
}) => {
  const { t } = useTranslation();
  const { allowed: createSubGoalsAllowed } = useCreateSubGoalAllowed();
  const { allowed: createTopLevelGoalAllowed } =
    useCreateUnlimitedGoalsAllowed();
  const { allowed: createProjectAllowed } = useCreateUnlimitedGoalsAllowed(
    GoalType.Project,
  );
  // No parent means a top-level goal in a life area, which has its own limit.
  const isSubGoal = !!parentIds.length;
  const createGoalAllowed = isSubGoal
    ? createSubGoalsAllowed
    : createTopLevelGoalAllowed;
  const openPremiumDialog = useOpenPremiumDialog();
  const openCreateGoalDialog = useOpenCreateGoalDialog();
  const openCreateProjectDialog = useOpenCreateProjectDialog();

  const close = () => onClose?.();
  const createOptions: CreateGoalOptions = deadline
    ? { parentIds, lifeAreaId, deadline }
    : { parentIds, lifeAreaId };

  const onCreateSubGoal = () => {
    if (!createGoalAllowed) {
      openPremiumDialog();
      close();
      return;
    }
    openCreateGoalDialog(createOptions);
    close();
  };

  const onCreateProject = () => {
    if (!createProjectAllowed) {
      openPremiumDialog();
      close();
      return;
    }
    openCreateProjectDialog(createOptions);
    close();
  };

  return (
    <>
      <PopupMenuListItem>
        <PopupMenuButton
          onClick={onCreateSubGoal}
          isPremium={!createGoalAllowed}
          start={<Icon icon={icon ?? Target} />}
        >
          {t(isSubGoal ? 'general.new-subgoal' : 'general.new-goal')}
        </PopupMenuButton>
      </PopupMenuListItem>
      <PopupMenuListItem>
        <PopupMenuButton
          onClick={onCreateProject}
          isPremium={!createProjectAllowed}
          start={<Icon icon={icon ?? Project} />}
        >
          {t('general.new-project')}
        </PopupMenuButton>
      </PopupMenuListItem>
    </>
  );
};
